import uuidv4 from 'uuid/v4';
import fetch from 'isomorphic-fetch';
import { get } from 'lodash';
import { addSeconds } from 'date-fns';
import config from 'config';
import { CookieProxy, clientCookies } from 'utils/cookies';
import ApiRedirectsHandler from 'utils/ApiRedirectsPreprocessor';
import { buildPoolCookie } from 'utils/poolCookie';
import { addQueryParams, stringToQuery } from 'utils/url';

const REQUEST_ID = 'RequestId';
const DNR_PARAM = 'dnr=y';
const FORWARDED_HEADERS = [
  'Host',
  'User-Agent',
  'X-Client-IP',
  'X-ClientIP', // F5 load balancers set incorrect name...
  REQUEST_ID,
];
export class ApiRedirectCausedRejection {}

export class Network {
  static processArgs(urlOrOpts, opts) {
    let url;
    let options;

    if (typeof urlOrOpts === 'string') {
      url = urlOrOpts;
      options = opts;
    } else {
      ({ url } = urlOrOpts);
      options = urlOrOpts;
    }

    return [
      url,
      options,
    ];
  }

  static isUrlValid(url) {
    return url && typeof url === 'string';
  }

  static handleStatus404(resp, router) {
    if (get(resp, 'status') === 404) {
      router.replace('/not-found');
    }
    return resp;
  }

  static composeFullServerUrl(request, url) {
    const { hostname, protocol } = request;
    const hostnameWithPortFromHeader = request.get('host');

    if (
      url.indexOf('http') === -1 && // is not a full url
      (hostnameWithPortFromHeader || hostname) &&
      protocol
    ) {
      return `${protocol}://${hostnameWithPortFromHeader || hostname}${url}`;
    }

    return url;
  }

  constructor(opts) {
    this.clientHeaders = {};
    this.processResponse = this.processResponse.bind(this);
    this.handleStatus404 = Network.handleStatus404;
    this.poolChangeRequest = false;
    this.apiRedirectsHandler = new ApiRedirectsHandler();

    if (typeof opts === 'object') {
      this.request = opts.request;
      this.response = opts.response;

      for (let i = 0, len = FORWARDED_HEADERS.length; i < len; i++) {
        const headerName = FORWARDED_HEADERS[i];
        const headerValue = opts.request.get(headerName);
        if (headerValue) {
          this.setProxyHeaders({
            [headerName]: headerValue,
          });
        }
      }
    }

    if (config.isServer && this.request) {
      // NOTE: Proxy already set "Cookie" header from browser request to the Webshop APIs
      // (eg. session cookies already set in the browser)
      this.cookieProxy = new CookieProxy(this.request);

      if (!opts.request.get(REQUEST_ID)) {
        this.setProxyHeaders({
          [REQUEST_ID]: uuidv4(),
        });
      }
    }
  }

  setStore(store) {
    this.store = store;
    this.apiRedirectsHandler.setStore(store);
  }

  setHistory(history) {
    this.routerHistory = history;
  }

  temporaryRedirect(url, status = 302) {
    if (this.response) {
      console.log(`[Set response redirect] to: ${url}`); // eslint-disable-line no-console
      this.response.preventCaching().redirect(status, url);
    } else {
      throw new Error('[Network] temporaryRedirect can be called server-side only');
    }
  }

  redirectTo(redirectOpts, isReplaceUrl) {
    const {
      type,
      reload,
      to: url,
      status = 302,
    } = redirectOpts;

    if (!url) {
      throw new Error(`[apiRedirectWarning] No url provided to apiRedirect (${type})`);
    }

    const isFullUrl = url.indexOf('http') === 0;

    if (config.isServer && this.request) {
      // Server header - Location should contain full uri (domain and protocol)
      this.temporaryRedirect(Network.composeFullServerUrl(this.request, url), status);
    } else if (isFullUrl || reload) {
      window.location.href = `${isFullUrl ? '' : window.location.origin}${url}`;
    } else if (status === 301) {
      this.routerHistory.replace(url);
    } else if (isReplaceUrl) {
      window.location.replace(`${isFullUrl ? '' : window.location.origin}${url}`);
    } else {
      this.routerHistory.push(url);
    }
  }

  setProxyHeaders(headers) {
    this.clientHeaders = { ...this.clientHeaders, ...headers };
  }

  setPoolSwitchCookie(response = null) {
    const cookie = buildPoolCookie(this.poolChangeRequest);
    if (response) {
      console.log('[X-Server-Pool-Changed] setting BP cookie'); // eslint-disable-line no-console
      response.append('Set-Cookie', cookie);
    }
  }

  setSPORCookie(currentUrl, response = null) {
    const { request } = this;
    return () => {
      const expiration = addSeconds(Date.now(), 10);
      if (response) {
        const fullUrl = Network.composeFullServerUrl(request, currentUrl);
        response.append('Set-Cookie', `SPOR="${fullUrl}"; Path=/; ${expiration}; secure; httpOnly;`);
      } else {
        clientCookies.set('SPOR', currentUrl, { path: '/', expires: expiration, secure: true, httpOnly: false });
      }
    };
  }

  static doSetCookieHeaderExists(headers, header) {
    const headerName = header.split(';')[0];
    return headers.some(singleHeader => {
      const singleHeaderName = singleHeader.split(';')[0];
      return (singleHeaderName.split('=')[0] === headerName.split('=')[0]);
    });
  }

  processResponse(fetchResponse) {
    const { headers } = fetchResponse;
    const poolChangeRequest = headers.get('X-Server-Pool-Changed');
    const currentUrl = this.request ? this.request.originalUrl : get(window, 'location.href', '');
    const dnr = currentUrl.includes(DNR_PARAM);
    if (poolChangeRequest && !dnr) {
      console.log(`[X-Server-Pool-Changed] header detected: ${poolChangeRequest}`); // eslint-disable-line no-console
      this.poolChangeRequest = poolChangeRequest;
      if (config.isServer) {
        const u = this.request.originalUrl;
        const redirectUrl = `${u}${u.includes('?') ? '&' : '?'}${DNR_PARAM}`;
        this.setPoolSwitchCookie(this.response);
        this.temporaryRedirect(redirectUrl);
        return new Promise().reject();
      }
    }
    const setCokie = 'Set-Cookie';
    const responseHeaders = this.response && this.response.get(setCokie) ? this.response.get(setCokie) : [];

    return new Promise((resolve, reject) => {
      if (config.isServer && this.cookieProxy) {
        // NOTE: Browsers handle the Set-Cookie headers on their own.
        // NOTE: Temporary store cookies coming from Webshop APIs
        // (resemble browser behaviour) for further API calls
        this.cookieProxy.storeApiResponseCookies(fetchResponse);
        this.cookieProxy.getApiResponseSetCookieHeaders()
          .forEach(setCookieHeader => {
            if (!Network.doSetCookieHeaderExists(responseHeaders, setCookieHeader)) {
              this.response.append('Set-Cookie', setCookieHeader);
            }
          });
      }
      const { status } = fetchResponse;
      const contentType = headers.get('Content-Type');
      let dataPromise;

      if (contentType && contentType.indexOf('application/json') >= 0) {
        dataPromise = fetchResponse.json();
      } else {
        dataPromise = fetchResponse.text();
      }
      dataPromise.then(data => {
        if (ApiRedirectsHandler.isPredefinedResponse(data)) {
          const callback = this.setSPORCookie(currentUrl, this.response);
          const redirect = this.apiRedirectsHandler.handleResponse({ currentUrl, data, status, callback });
          try {
            if (redirect) {
              this.redirectTo(redirect);
            } else {
              console.log('[APIRedirect] Unknown redirect action', redirect); // eslint-disable-line no-console
            }
          } catch (ex) {
            console.log(ex); // eslint-disable-line no-console
          }
          reject(new ApiRedirectCausedRejection());
        } else if (status >= 200 && status < 400) {
          resolve(data);
        } else {
          reject({ data, status, statusText: fetchResponse.statusText, url: fetchResponse.url, info: true });
        }
      }, reason => {
        reject({ reason, status, statusText: `Data promise failure, reason: ${reason}`, url: fetchResponse.url });
      });
    });
  }

  getState() {
    if (this.store) { // check needed to don't complicate network usage in unit tests
      return this.store.getState();
    }
    return null;
  }

  buildRequestHeaders(options, isExternalRequest = false) {
    const cookieHeaders = this.cookieProxy ? { Cookie: this.cookieProxy.formatCookiesForApiRequest() } : {};

    const state = this.getState();
    const pageViewId = get(state, 'analytics.pageView.id', '');
    const pageViewUrl = get(state, 'analytics.pageView.url', '');

    const reqHeaders = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...cookieHeaders,
      ...this.clientHeaders,
      ...options.headers,
    };

    if (!isExternalRequest) {
      reqHeaders['X-Page-View-Id'] = pageViewId;
      reqHeaders['X-Original-URI'] = pageViewUrl;
    }

    return reqHeaders;
  }

  getAllowedQueryParamsFromRequest = (proxyQueryParams) => {
    const hasRequestQuery = config.isServer && this.request && this.request.query;
    const hasWindowQuery = !config.isServer && typeof window !== 'undefined' && window.location.search;
    const hasAnyQuery = hasRequestQuery || hasWindowQuery;

    if (!hasAnyQuery || !proxyQueryParams) {
      return {};
    }
    const query = config.isServer ? this.request.query : stringToQuery(window.location.search);
    return proxyQueryParams
      .filter(paramKey => query[paramKey] !== undefined)
      .reduce((params, paramKey) => ({
        ...params,
        [paramKey]: query[paramKey],
      }), {});
  };

  makeRequest(urlOrOpts, opts = {}, isExternal = false) {
    const [url, options] = Network.processArgs(urlOrOpts, opts);
    if (!Network.isUrlValid(url)) {
      throw new Error('[Network] No url argument or url argument is not a string');
    }

    const fetchOpts = {
      ...options,
      credentials: isExternal ? 'omit' : 'include',
      headers: this.buildRequestHeaders(options, isExternal),
    };

    // IE doesn't accept defined BODY in GET request
    // https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/8546263/
    if (typeof fetchOpts.body === 'object' && options.method !== 'GET') {
      fetchOpts.body = JSON.stringify(fetchOpts.body);
    }

    const { proxyQueryParams } = options;
    const urlWithParams = proxyQueryParams
      ? addQueryParams(url, this.getAllowedQueryParamsFromRequest(proxyQueryParams))
      : url;

    return fetch(urlWithParams, fetchOpts)
      .then(this.processResponse);
  }

  get(url, opts, isExternalRequest) {
    const options = { ...opts, method: 'GET' };
    return this.makeRequest(url, options, isExternalRequest);
  }

  head(url, opts) {
    const options = { ...opts, method: 'HEAD' };
    return this.makeRequest(url, options);
  }

  post(url, opts) {
    const options = { ...opts, method: 'POST' };
    return this.makeRequest(url, options);
  }

  put(url, opts) {
    const options = { ...opts, method: 'PUT' };
    return this.makeRequest(url, options);
  }

  patch(url, opts) {
    const options = { ...opts, method: 'PATCH' };
    return this.makeRequest(url, options);
  }

  del(url, opts) {
    const options = { ...opts, method: 'DELETE' };
    return this.makeRequest(url, options);
  }
}

// Prevent instance export server side. It would cause unexpected issues if used server side.
const instance = (typeof window !== 'undefined') ? new Network() : null;
export default instance;
